import { getStartOf, parseDateFromISO8601 } from './dates';
import { isPublicMoorageListing } from './listingHelpers';
import { BOOKING_STATE_ACCEPTED, BOOKING_STATE_PENDING } from './types';

const DEFAULT_TIME_ZONE = 'Etc/UTC';
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ACTIVE_BOOKING_STATES = [BOOKING_STATE_PENDING, BOOKING_STATE_ACCEPTED];
const INVALID_CHECK_IN_STATE_PATTERN = /cancel|declin|reject|expire|closed|inactive|complete|ended|checked[ _-]?out/i;

const validDate = date => (date instanceof Date && !Number.isNaN(date.getTime()) ? date : null);

const unwrapDateValue = value =>
  value && typeof value === 'object' && !(value instanceof Date)
    ? value.date || value.value || value.timestamp
    : value;

export const getListingTimeZone = listing =>
  listing?.attributes?.availabilityPlan?.timezone ||
  listing?.attributes?.publicData?.timeZone ||
  DEFAULT_TIME_ZONE;

export const parseUpcomingDate = (value, timeZone = DEFAULT_TIME_ZONE) => {
  const rawValue = unwrapDateValue(value);

  if (rawValue instanceof Date) {
    return validDate(rawValue);
  }
  if (typeof rawValue === 'string' && DATE_ONLY_PATTERN.test(rawValue.trim())) {
    return validDate(parseDateFromISO8601(rawValue.trim(), timeZone));
  }

  return rawValue != null ? validDate(new Date(rawValue)) : null;
};

export const getUpcomingThreshold = (listing, now = new Date()) =>
  getStartOf(now, 'day', getListingTimeZone(listing));

export const getBookingStartDate = transaction =>
  parseUpcomingDate(transaction?.booking?.attributes?.start);

export const getTransactionCheckInDate = (transaction, listing) => {
  const attributes = transaction?.attributes || {};
  const checkInDate =
    transaction?.booking?.attributes?.checkInDate ||
    attributes.protectedData?.checkInDate ||
    attributes.metadata?.checkInDate ||
    attributes.publicData?.checkInDate;

  return parseUpcomingDate(checkInDate, getListingTimeZone(listing));
};

export const getTransactionUpcomingDate = (transaction, listing) => {
  const publicData = listing?.attributes?.publicData || {};

  return isPublicMoorageListing(publicData)
    ? getTransactionCheckInDate(transaction, listing) || getBookingStartDate(transaction)
    : getBookingStartDate(transaction);
};

const isValidUpcomingTransaction = (transaction, listing, now) => {
  const bookingState = transaction?.booking?.attributes?.state;
  const date = getTransactionUpcomingDate(transaction, listing);

  return (
    ACTIVE_BOOKING_STATES.includes(bookingState) &&
    date &&
    date >= getUpcomingThreshold(listing, now)
  );
};

export const selectNextUpcomingTransaction = (transactions, listing, now = new Date()) =>
  (transactions || []).reduce((nearest, transaction) => {
    if (!isValidUpcomingTransaction(transaction, listing, now)) {
      return nearest;
    }

    const date = getTransactionUpcomingDate(transaction, listing);
    const nearestDate = nearest ? getTransactionUpcomingDate(nearest, listing) : null;
    return !nearestDate || date < nearestDate ? transaction : nearest;
  }, null);

const getCheckInDateValue = checkIn =>
  checkIn?.checkInDate ||
  checkIn?.arrivalTime ||
  checkIn?.checkedInAt ||
  checkIn?.startTime ||
  checkIn?.createdAt;

export const getCheckInDate = (checkIn, listing) =>
  parseUpcomingDate(getCheckInDateValue(checkIn), checkIn?.timeZone || getListingTimeZone(listing));

const getListingCheckIns = listing => {
  const publicData = listing?.attributes?.publicData || {};
  const checkIns = Array.isArray(publicData.publicMoorageCheckIns)
    ? publicData.publicMoorageCheckIns
    : [];
  const activeCheckIn = publicData.publicMoorageCheckIn;
  const recordsById = new Map();

  checkIns.forEach((checkIn, index) => {
    recordsById.set(checkIn?.id || `history-${index}`, checkIn);
  });
  if (activeCheckIn) {
    recordsById.set(activeCheckIn.id || 'active-check-in', activeCheckIn);
  }

  return Array.from(recordsById.values());
};

const isValidUpcomingCheckIn = (checkIn, listing, now) => {
  const stateValues = [checkIn?.state, checkIn?.status].filter(Boolean);
  const hasInvalidState = stateValues.some(value =>
    INVALID_CHECK_IN_STATE_PATTERN.test(String(value))
  );
  const hasEnded = Boolean(checkIn?.endedAt || checkIn?.departureTime);
  const date = getCheckInDate(checkIn, listing);

  return !hasInvalidState && !hasEnded && date && date >= getUpcomingThreshold(listing, now);
};

export const selectNextUpcomingCheckIn = (listing, now = new Date()) => {
  const publicData = listing?.attributes?.publicData || {};
  if (!isPublicMoorageListing(publicData)) {
    return null;
  }

  return getListingCheckIns(listing).reduce((nearest, checkIn) => {
    if (!isValidUpcomingCheckIn(checkIn, listing, now)) {
      return nearest;
    }

    const date = getCheckInDate(checkIn, listing);
    const nearestDate = nearest ? getCheckInDate(nearest, listing) : null;
    return !nearestDate || date < nearestDate ? checkIn : nearest;
  }, null);
};

export const getUpcomingBookingForListing = (listing, transactions, now = new Date()) => {
  const publicData = listing?.attributes?.publicData || {};
  const nextTransaction = selectNextUpcomingTransaction(transactions, listing, now);

  if (!isPublicMoorageListing(publicData)) {
    return nextTransaction
      ? {
          type: 'booking',
          date: getTransactionUpcomingDate(nextTransaction, listing),
          timeZone: getListingTimeZone(listing),
          transaction: nextTransaction,
          checkIn: null,
        }
      : null;
  }

  const nextCheckIn = selectNextUpcomingCheckIn(listing, now);
  const transactionDate = nextTransaction
    ? getTransactionUpcomingDate(nextTransaction, listing)
    : null;
  const checkInDate = nextCheckIn ? getCheckInDate(nextCheckIn, listing) : null;

  if (checkInDate && (!transactionDate || checkInDate <= transactionDate)) {
    return {
      type: 'checkIn',
      date: checkInDate,
      timeZone: nextCheckIn?.timeZone || getListingTimeZone(listing),
      transaction: null,
      checkIn: nextCheckIn,
    };
  }
  return nextTransaction
    ? {
        type: 'checkIn',
        date: transactionDate,
        timeZone: getListingTimeZone(listing),
        transaction: nextTransaction,
        checkIn: null,
      }
    : null;
};

export const getUpcomingBookingsByListingId = (listings, transactions, now = new Date()) => {
  const transactionsByListingId = (transactions || []).reduce((byListingId, transaction) => {
    const listingId = transaction?.listing?.id?.uuid;
    if (listingId) {
      byListingId[listingId] = byListingId[listingId] || [];
      byListingId[listingId].push(transaction);
    }
    return byListingId;
  }, {});

  return (listings || []).reduce((byListingId, listing) => {
    const listingId = listing?.id?.uuid;
    const upcomingBooking = listingId
      ? getUpcomingBookingForListing(listing, transactionsByListingId[listingId] || [], now)
      : null;

    if (listingId && upcomingBooking) {
      byListingId[listingId] = upcomingBooking;
    }
    return byListingId;
  }, {});
};
